import {Box, IconButton, Popover, Slider, SpeedDialAction, SvgIcon, Typography} from "@mui/material";

import {
  Fullscreen,
  FullscreenExit,
  LinkOffOutlined,
  Settings,
  SportsEsports,
  VolumeDown,
  VolumeOff,
  VolumeUp,
} from "@mui/icons-material";
import {useNavigate} from "react-router-dom";
import {useEffect, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {setVolume} from "../../redux/AudioSlice";
import KeyboardInput from "./inputs/KeyboardInput";
import GamepadInput from "./inputs/GamepadInput";
import AbsoluteKeyboardInput from "./inputs/AbsoluteKeyboardInput";
import ControlFab from "./ControlFab";
import WakeLock from "./util/WakeLock";
import {setClientConfig} from "../../redux/ClientSettingsSlice";
import OverlaySettings from "./OverlaySettings";
import ShowMetrics from "./ShowMetrics";
import NetworkStatus from "./NetworkStatus";

const VolumeControl = () => {

  const [openVolumeBar, setOpenVolumeBar] = useState(false);
  const volume = useSelector(state => state.audio.volume);
  const dispatch = useDispatch();
  const anchorEl = useRef();
  return <>
    <IconButton ref={anchorEl} onClick={() => setOpenVolumeBar(true)}
                sx={{my: 2, mr: 12}}>
      {volume === 0 ? <VolumeOff/> : <VolumeUp/>}
    </IconButton>
    <Popover slotProps={{
      paper: {

        sx: {
          px: 2, display: 'flex',
          flexDirection: "column",
          alignItems: 'center',
          justifyContent: 'center'
        },
      }
    }} anchorOrigin={{vertical: 'bottom', horizontal: 'left'}} open={openVolumeBar}
             onClose={() => setOpenVolumeBar(false)}
             anchorEl={anchorEl.current}>
      <VolumeUp sx={{my: 2}}/>
      <Slider sx={{height: "150px"}} orientation={"vertical"} min={0} max={100} step={10}
              onChange={(e, v) => dispatch(setVolume(v))}
              value={volume}/>
      <VolumeDown sx={{my: 2}}/>
    </Popover>
  </>
}

const Overlay = ({children, cli, inputReady}) => {
  const [open, setOpen] = useState(false);
  const [fabOpen, setFabOpen] = useState(false);
  const [inControl, setInControl] = useState(false);
  const [menuOpen, setMenuOpen] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const dispatch = useDispatch();
  const [fullscreen, setFullscreen] = useState(false);
  const moveMult = useSelector(state => state.client_settings.movement_multiplier);
  const scrollMult = useSelector(state => state.client_settings.scroll_multiplier);
  const cursorCap = useSelector(state => state.client_settings.cursor_capture);
  const clientSettings = useSelector(state => state.client_settings);
  const gamepadButton = useRef(null);
  const navigate = useNavigate();
  const pinMetrics = useSelector(state => state.metrics.pinMetrics)
  const toggleFullscreen = () => {
    setFullscreen(!fullscreen)
  }
  const changeTakeControl = (value) => {
    setInControl(value)
    if (value) {
      setFullscreen(value)
    }
  }
  const handleFullscreenchange = () => {
    setFullscreen(!!document.fullscreenElement)
  }
  useEffect(() => {
    if (fullscreen && !document.fullscreenElement) {
      let body = document.querySelector('body')
      if (body.requestFullscreen) {
        body.requestFullscreen().catch(e => {
          console.warn("failed to get to fullscreen")
        })
      }
    } else if ((!fullscreen) && document.fullscreenElement) {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  }, [fullscreen]);

  useEffect(() => {
    document.addEventListener("fullscreenchange", handleFullscreenchange)
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenchange)
    }
  }, [])

  const toggleOpen = () => setOpen(!open);
  const openGamepadMenu = (menuId) => {
    return (e) => {
      setAnchorEl(e.currentTarget);
      setMenuOpen(menuId)
    };
  }


  const toggleCursorCapture = () => {
    dispatch(setClientConfig({...clientSettings, cursor_capture: !cursorCap}))
  }
  const closeGamepadMenu = () => {
    setMenuOpen("");
  }

  const IconLogo =
    <SvgIcon>
      <svg xmlns="http://www.w3.org/2000/svg"
           version="1.1" viewBox="637.85 233.45 641.83 607" style={{width: 40, height: 35}}>
        <path
          d="M853.25,439.28c24.25,0,29.55,30.42,57.51,32.71,29.98-1.18,60.63,1.74,90.51.53,13.36-.54,21.51-2.66,32.19-10.81,8.82-6.73,15.54-18.27,26.66-21.34,12.5-3.45,32,2.49,44.38,6.38s26.73,8.51,31.39,22.11c1.68,4.9.56,8.31,1.1,12.9s9.19,8.56,12.01,11.99,6.97,10.11,9.4,14.1c33.04,54.14,26.73,122.52-14.65,170.16-18.62,21.43-53.2,33.9-80.1,21.08-18.57-8.85-27.33-30.78-47.78-36.22-13.21-3.51-28.12-1.97-41.61-1.85-21.4.19-43.29-.3-64.53.47-30.1,1.1-30.04,10.87-49.74,28.26-24.16,21.33-50.72,16.59-77.02,2.5-7.78-4.17-12.61-6.88-18.19-13.81-39.54-49.14-40.83-120.19-4.71-171.61,2.6-3.7,7.26-10.07,10.38-13.12,2.7-2.63,10.22-6.53,11.73-9.27s.92-7.22,1.35-10.15c.88-5.99,3.91-12.04,8.43-16.07,10.73-9.56,46.94-18.97,61.26-18.96ZM1128.5,478.5c.84-6.1-2.07-10.92-6.83-14.42-9.67-7.1-43.97-16.44-55.89-16.09-5.87.17-11.24,4.95-14.76,9.26,0,1.98,14.79,1.11,16.76,1.23,13.17.79,29.57,6.15,41.71,11.29,5.26,2.22,11.93,6.68,16.65,8.35.8.28,1.5.51,2.36.39ZM792,478.5c.63.65,14.38-7.06,16.32-7.93,13.84-6.2,32.26-11.12,47.41-12.09,1.92-.12,14.14.71,14.26-1.23-4.67-7.59-10.16-9.15-18.76-8.77-11.17.49-46.92,9.24-54.5,17-3.07,3.15-5.19,8.62-4.73,13.02ZM849.82,499.68c-.78-.66-1.51-1.07-2.55-1.2-3.86-.47-20.47-.6-24,.05-1.54.28-7.11,5.51-7.55,6.95v24.04c2.5,3.15,12.44,18.39,15.15,19.37,1.68.61,6.8,1.03,8.33.07l15.08-18.92c.5-6.25.11-12.54.2-18.8.04-2.67,1.38-4.12-.48-6.98-.26-.41-3.76-4.21-4.18-4.57ZM1064.87,510.62c-5.4.91-12.42,7.82-14.02,12.98-6.51,20.99,22.13,34.64,34.37,16.87,10.41-15.12-3.18-32.74-20.35-29.85ZM798.91,541.12c-.58.01-1.33.39-2.14.4-4.96.06-13.61-.8-18,0-1.96.36-4.07,3.17-5.51,4.49l-.77,28.25c1.23,3.66,2.51,6.9,6.74,7.26,7.53.63,16.17-.33,23.8-.24,3.15-3.35,18.8-12.21,20.09-15.41.7-1.73.75-7.91-.4-9.34l-22.11-15.44c-.58-.23-1.17.02-1.7.03ZM866.86,541.61c-1.5.13-4.56,2.99-5.87,4.13-4.01,3.52-7.71,7.71-11.72,11.28-1.43,2.28-.76,6.79-.51,9.47l17.63,14.37c5.38,1.1,10.91.51,16.38.62,2.55.05,5.12.94,7.95.49,2.29-.36,6.4-4.89,6.73-7.27.8-5.61-.06-13.98.03-19.98.04-2.77,1.54-5.09-.55-7.91-.76-1.02-5.01-4.6-6.21-4.79-5.36-.85-15.11-.03-20.99,0-.98,0-1.92-.5-2.87-.41ZM1030.89,543.64c-16.2,1.72-24.46,22.51-11.39,34.11,17.94,15.94,42.06-9.11,28.26-26.26-4.07-5.05-10.23-8.56-16.87-7.85ZM1102.37,544.12c-19.92,3.1-20.08,35.26.51,38.25,29.69,4.31,29.88-42.98-.51-38.25ZM831.86,575.61c-.89.2-1.49.78-2.12,1.38-1.63,1.55-13.56,16.35-13.92,17.62l.41,24.92c1.4,1.98,3.7,4.91,6.15,5.35,3.23.58,21.67.64,24.77,0,1.6-.33,6.54-4.02,6.85-5.65l-.09-24.88-12.92-18.08c-1.35-1.08-7.37-1.06-9.12-.67ZM1065.37,581.62c-5.81.91-12.27,6.82-14.26,12.24-6.21,16.93,12.13,32.94,27.73,23.73,18.85-11.12,8.41-39.39-13.48-35.98Z"/>
        <path
          d="M1194.12,462.12c2.29-2.2,17.43,1.72,21.2,2.31,2.98.46,6.25.89,9.17.83-2.73-7.84-4.48-15.99-7.14-23.86-11.18-33.12-31.29-65.51-54.6-91.4-9.38-10.42-19.04-17.96-30.31-26.19-98.47-71.86-203.79-90.11-312.6-26.21-28.21,16.57-54.04,35.8-74.83,61.17-26.98,32.92-46.35,71.51-55.51,113.23,2.85-.43,5.76-.66,8.6-1.14,2.53-.43,19.25-4.77,19.92-2.02-2.65,17.86-2.26,35.92-2.5,53.94-.81,59.72,3.29,119.68,17.63,177.83,1.27,5.14,4.6,20.32,7.14,23.86,2.01,2.81,13.49,10.43,17.06,12.94,39.76,27.93,80.36,46.73,127.75,58.25,13.99,3.4,28.64,6.6,43.06,6.74,3.5-2.38,4.74-7.12,8.87-8.63,4.78-1.76,9.99-.3,14.72-.24,16.72.2,39.79-4.48,46.86,15.64,4.15,11.79-4.09,28.97-16.97,30.71-12.17,1.65-27.33-.92-39.82.08-5.15-.6-9.36-4.25-11.82-8.68-1.37-2.47-1.5-5.97-4.57-6.93-6.71-2.11-18.05-2.62-25.51-3.99-59.99-10.94-115.95-36.2-163.86-73.64-4.41-3.45-16.76-13.54-18.96-18.04-1.37-2.79-1.35-6.1-2.55-8.95-16.88.58-32.91-1.65-46.04-12.96-19.06-16.42-20.8-39.03-23.93-62.57-4.93-37.04-8.18-80.68-6.08-117.96,1.09-19.47,12.05-35.38,27.42-46.58,8.36-59.81,37.33-121.89,83.09-161.91,50.55-44.21,119.94-79.08,187.76-83.74,76.65-5.26,159.52,27.23,219.19,73.8,50.98,39.78,84.19,101.95,93.81,165.69,15.91,10.22,28.18,26.87,29.27,46.23,2.05,36.43-1.08,78.8-6.09,114.95-3.84,27.69-5.97,54.53-32.47,70.03-20.14,11.78-42.64,7.79-64.99,8.07-.51-.36-.43-.97-.49-1.51-1.08-8.97,5.77-29.73,7.87-39.68,13.22-62.48,17.89-136.25,12.15-199.84-.17-1.84-1.85-8.7-.88-9.63Z"/>
      </svg>
    </SvgIcon>

  return <>
    {children}
    {inControl && (cursorCap ?
      <KeyboardInput cli={cli} inControl={inControl} onControlChange={setInControl} move_multiplier={moveMult}
                     scroll_multiplier={scrollMult}/> : <AbsoluteKeyboardInput
        cli={cli} inControl={inControl} onControlChange={changeTakeControl} move_multiplier={moveMult}
        scroll_multiplier={scrollMult}
      />)
    }
    <WakeLock/>
    <GamepadInput open={menuOpen === "gamepad_menu"} anchorEl={anchorEl} cli={cli}
                  inputReady={inputReady} onClose={closeGamepadMenu}/>
    <Box sx={{bgcolor: "rgba(0,0,0,0.2)", display: inControl ? "none" : "flex"}} position={"absolute"} top={0}
         left={0}
         height={"100%"}
         width={"100%"}
         flexDirection={"column"} justifyContent={"center"} alignItems={"center"}>
      <Typography variant={"h3"} color={"textSecondary"}><Typography component={'span'} variant="h3"
                                                                     color={"textPrimary"}>Double
        click</Typography> to
        take control</Typography>

      <Typography variant={"h3"} color={"textSecondary"}><Typography component={'span'} variant="h3"
                                                                     color={"textPrimary"}>Ctrl +
        M</Typography> to release control</Typography>
    </Box>
    <Box sx={{display: inControl ? "none" : "flex"}} flexDirection={"column"} position={"absolute"} height={"100%"}
         width={"100%"} justifyContent={"space-between"} onDoubleClick={(e) => {
      console.log(e)
      if (!open) {
        changeTakeControl(true)
      }
    }}>
      <Box color={"#fff"} display={"flex"} justifyContent={"flex-end"} flexDirection={"row"}>
        <VolumeControl/>
      </Box>
    </Box>

    {(pinMetrics || !inControl) && <ShowMetrics/>}
    <NetworkStatus/>

    <ControlFab ariaLabel={"Overlay Control"} onOpen={() => {
      setFabOpen(true)
      setInControl(false)
    }} onClose={() => setFabOpen(false)}
                open={fabOpen}
                icon={IconLogo}
                FabProps={{color: "primary", size: "small"}}
                renderChildren={(tooltipPlacement) => {
                  return [<SpeedDialAction
                    key={"fullscreen"}
                    icon={fullscreen ? <FullscreenExit/> :
                      <Fullscreen/>}
                    onClick={toggleFullscreen}
                    tooltipTitle={fullscreen ? "Windowed" : "Fullscreen"}
                    tooltipOpen={true}
                    tooltipPlacement={tooltipPlacement}
                    color={"primary"}
                  />,
                    <SpeedDialAction
                      key={"gamepad"}
                      onClick={openGamepadMenu("gamepad_menu")}
                      ref={gamepadButton}
                      icon={<SportsEsports/>}
                      tooltipTitle={"Gamepads"}
                      tooltipOpen={true}
                      tooltipPlacement={tooltipPlacement}
                      color={"primary"}
                    />,
                    <SpeedDialAction
                      key={"disconnect"}
                      onClick={() => navigate("/")}
                      icon={<LinkOffOutlined/>}
                      tooltipTitle={"Disconnect"}
                      tooltipOpen={true}
                      tooltipPlacement={tooltipPlacement}
                      color={"primary"}
                    />,
                    <SpeedDialAction
                      key={"settings"}
                      onClick={toggleOpen}
                      icon={<Settings/>}
                      tooltipTitle={"Settings"}
                      tooltipOpen={true}
                      tooltipPlacement={tooltipPlacement}
                      color={"primary"}
                    />]
                }}
    />
    <OverlaySettings open={open} onClose={toggleOpen}/>
  </>
}


export default Overlay;